import { currentUserId } from '../engine/context'
import { findRow, readTable } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { fail, requireRole, roleCodesOf, txLog } from './c07.shared'

/**
 * INV-02 · detail label FG dan cetak ulang stiker (v7.50 M-01 / U-18, prototype `JalStiker.cetakUlangFg`).
 * - `fg-label?code=` — info label, Isi & Turunan (dari konversi SKU), Log Transaksi label, kelayakan cetak ulang.
 * - `fg-reprint` — mencatat cetak ulang di `tx_log` (aksi `fg-cetak-ulang`). Kode yang dicetak adalah `label_no` ALM apa adanya.
 * Tidak ada tabel baru: riwayat cetak ulang dibaca dari `tx_log` (usulan tabel `fg_label_reprint` = ERD-EXTENSION D86).
 * Salinan FE: `FG_REPRINT_*` di service-library `inventoryReports.constants.ts` (dicek uji paritas).
 */

type Params = Record<string, unknown> | undefined
const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')
const num = (value: unknown) => Number(value ?? 0)

export const FG_REPRINT_ROLES = ['admin_wh_fg', 'supervisor_wh_fg'] as const
export const FG_VIEW_ROLES = ['admin_wh_fg', 'operator_wh_fg', 'supervisor_wh_fg', 'kadiv_wh_fg', 'sales_marketing']
export const FG_REPRINT_MAX = 50
export const FG_REPRINT_OTHER = 'Lainnya'
export const FG_REPRINT_REASONS = [
  'Stiker rusak / terkelupas',
  'Stiker terlepas / hilang',
  'QR tidak terbaca',
  FG_REPRINT_OTHER
] as const
const MSG = {
  role: 'Cetak ulang stiker FG hanya untuk Admin dan Supervisor WH FG.',
  shipped: 'Unit sudah dikirim, stikernya tidak bisa dicetak ulang.',
  missing: 'Unit tidak ditemukan di Stok FG.',
  reason: 'Alasan wajib dipilih.',
  note: 'Tulis keterangan alasan (minimal 3 huruf).',
  copies: `Jumlah stiker 1 sampai ${FG_REPRINT_MAX}.`
} as const

const STATUS_LABEL: Record<string, string> = {
  LABELED: 'Berlabel (belum masuk rak)',
  IN_STOCK: 'Di gudang',
  ALLOCATED: 'Dialokasikan pesanan',
  STAGED: 'Di area staging',
  SHIPPED: 'Dikirim',
  CONSOLIDATION_RESERVED: 'Dipesan penggabungan',
  MERGE_RESERVED: 'Dipesan penggabungan',
  MERGED: 'Sudah digabung',
  MISSING: 'Hilang (opname)',
  ADJUSTED_NOT_FOUND: 'Disesuaikan (tidak ditemukan)',
  CLAIM_RECEIVED: 'Diterima dari claim',
  GONE: 'Dikirim',
  RESERVED: 'Dipesan penggabungan'
}

type Found = { kind: 'label' | 'pack'; row: LocalRow }

function find(code: string): Found | null {
  const label = readTable('fg_label').find(row => String(row.label_no) === code)
  if (label) return { kind: 'label', row: label }
  const pack = readTable('fg_pack_stock').find(row => String(row.label_no) === code)
  return pack ? { kind: 'pack', row: pack } : null
}

const isShipped = (found: Found) =>
  found.kind === 'label'
    ? found.row.label_status === 'SHIPPED'
    : !(num(found.row.qty_pack) > 0) || found.row.status === 'GONE'

function conversionOf(sku: LocalRow | undefined) {
  return {
    pack_per_slop: num(sku?.pack_per_slop) || 10,
    slop_per_bal: num(sku?.slop_per_bal) || 20,
    bal_per_karton: num(sku?.bal_per_karton) || 4
  }
}

const userName = (id: unknown) => {
  const user = findRow('directus_users', idOf(id))
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ')
}

const canReprint = () =>
  roleCodesOf(currentUserId()).some(code => (FG_REPRINT_ROLES as readonly string[]).includes(code))

function requireFgScope() {
  if (!roleCodesOf(currentUserId()).some(code => FG_VIEW_ROLES.includes(code)))
    fail('Label ini ada di Gudang Finish Good, di luar cakupan peran Anda.', 'ERR_UNAUTHORIZED_ROLE', 403)
}

export function fgLabelDetail(params: Params) {
  requireFgScope()
  const code = String(params?.code ?? '').trim()
  const found = code ? find(code) : null
  if (!found) fail(MSG.missing, 'ERR_NOT_FOUND', 404)
  const { kind, row } = found
  const sku = findRow('mt_product', idOf(row.sku_id))
  const batch = findRow('fg_labeler_batch', idOf(row.batch_id))
  const conv = conversionOf(sku)
  const level = (kind === 'pack' ? 'Pack' : String(row.level)) as 'Karton' | 'Bal' | 'Slop' | 'Pack'
  const perUnit = {
    Karton: conv.pack_per_slop * conv.slop_per_bal * conv.bal_per_karton,
    Bal: conv.pack_per_slop * conv.slop_per_bal,
    Slop: conv.pack_per_slop,
    Pack: 1
  }
  const qtyPack = kind === 'pack' ? num(row.qty_pack) : perUnit[level]
  const pallet = findRow('pallet', idOf(row.pallet_id))
  const container = findRow('container', idOf(row.container_id))
  const zone = findRow('warehouse_zone', idOf(row.zone_id ?? pallet?.zone_id))
  const status = String(kind === 'pack' ? row.status : row.label_status)
  const shipped = isShipped(found)
  const plan = findRow('delivery_plan', idOf(row.shipped_plan_id))
  const retur = kind === 'pack' ? String(row.origin) === 'Retur' : Boolean(row.is_return)

  const contents =
    level === 'Karton'
      ? [
          { level: 'Bal', count: conv.bal_per_karton, equivalent_pack: perUnit.Bal },
          { level: 'Slop', count: conv.bal_per_karton * conv.slop_per_bal, equivalent_pack: perUnit.Slop }
        ]
      : level === 'Bal'
        ? [{ level: 'Slop', count: conv.slop_per_bal, equivalent_pack: perUnit.Slop }]
        : []
  const children = readTable('fg_label')
    .filter(child => idOf(child.parent_label_id) === idOf(row.id) && kind === 'label')
    .map(child => ({ code: String(child.label_no), level: String(child.level), status: String(child.label_status) }))
  const parent = kind === 'label' ? findRow('fg_label', idOf(row.parent_label_id)) : undefined

  const batchNo = String(batch?.batch_no ?? '')
  const rows = readTable('tx_log').filter(entry => {
    const ref = String(entry.doc_ref ?? '')
    return ref === code || String(entry.detail ?? '').includes(code) || (batchNo !== '' && ref === batchNo)
  })
  const log = rows.map(entry => ({
    id: String(entry.id),
    ts: String(entry.ts),
    action: String(entry.action),
    doc_ref: (entry.doc_ref as string | null) ?? null,
    detail: (entry.detail as string | null) ?? null,
    user_name: userName(entry.user_id),
    warehouse: (findRow('warehouse', idOf(entry.warehouse_id))?.code as string | undefined) ?? null,
    scope: String(entry.doc_ref ?? '') === batchNo && batchNo !== '' ? ('batch' as const) : ('label' as const)
  }))
  if (kind === 'label' && row.date_created)
    log.push({
      id: `created-${String(row.id)}`,
      ts: String(row.date_created),
      action: 'alm-label',
      doc_ref: code,
      detail: `Label ${level} dicetak Auto Labeler untuk batch ${batchNo}`,
      user_name: userName(row.user_created),
      warehouse: 'WH-FG',
      scope: 'label'
    })
  log.sort((a, b) => b.ts.localeCompare(a.ts))

  const reprints = rows.filter(entry => entry.action === 'fg-cetak-ulang' && String(entry.doc_ref) === code)
  const copies = reprints.reduce(
    (sum, entry) => sum + (Number(/cetak ulang (\d+) stiker/.exec(String(entry.detail))?.[1]) || 0),
    0
  )

  const allowed = canReprint()
  return {
    code,
    kind,
    level,
    sku_code: String(sku?.sku_code ?? sku?.code ?? ''),
    sku_name: String(sku?.name ?? ''),
    sku_type: (sku?.type as string | undefined) ?? null,
    batch_no: batchNo,
    status,
    status_label: shipped ? 'Dikirim' : (STATUS_LABEL[status] ?? status),
    location: String(pallet?.code ?? container?.code ?? zone?.code ?? '') || null,
    zone_code: (zone?.code as string | undefined) ?? null,
    qty_pack: qtyPack,
    equivalent_pack: qtyPack,
    is_return: retur,
    is_repack: kind === 'pack' && String(row.origin) === 'REPACK',
    shipped,
    shipment: shipped
      ? {
          sj_no: (plan?.sj_no as string | undefined) ?? null,
          customer: (findRow('mt_companies', idOf(plan?.customer_id))?.name as string | undefined) ?? null
        }
      : null,
    parent_code: parent ? String(parent.label_no) : null,
    conversion: conv,
    contents,
    children,
    can_reprint: allowed && !shipped,
    reprint_block: !allowed ? MSG.role : shipped ? MSG.shipped : null,
    reprint_count: reprints.length,
    reprint_copies: copies,
    log
  }
}

export function fgReprint(body: { code?: string; copies?: number; reason?: string; note?: string }) {
  requireRole(MSG.role, ...FG_REPRINT_ROLES)
  const code = String(body.code ?? '').trim()
  const found = code ? find(code) : null
  if (!found) fail(MSG.missing, 'ERR_NOT_FOUND', 404)
  if (isShipped(found)) fail(MSG.shipped)
  const raw = Number(body.copies)
  const copies = Math.ceil(raw)
  if (!(copies >= 1 && copies <= FG_REPRINT_MAX)) fail(MSG.copies)
  const reason = String(body.reason ?? '').trim()
  if (!reason) fail(MSG.reason)
  if (!(FG_REPRINT_REASONS as readonly string[]).includes(reason)) fail('Alasan tidak dikenal.')
  const note = String(body.note ?? '').trim()
  if (reason === FG_REPRINT_OTHER && note.length < 3) fail(MSG.note)
  const text = reason === FG_REPRINT_OTHER ? `${FG_REPRINT_OTHER} — ${note}` : reason
  const level = found.kind === 'pack' ? 'Pack' : String(found.row.level)
  const sku = findRow('mt_product', idOf(found.row.sku_id))
  txLog(
    'FG Stiker',
    'INV-02',
    'fg-cetak-ulang',
    code,
    `${level} ${String(sku?.sku_code ?? sku?.code ?? '')} — cetak ulang ${copies} stiker, alasan: ${text}`
  )
  return { code, level, copies, reason: text, printed_at: new Date().toISOString() }
}
